import React, { useState } from "react"
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
} from "../../../shared/components/ui/dialog"
import { Button } from "../../../shared/components/ui/button"
import { Dialog, DialogTrigger } from "@radix-ui/react-dialog"
import { Trash } from "lucide-react"
import { deleteIconPng } from "../../../shared/constants/images"
import Image from "next/image"
import { deleteWorkOrder } from "@/features/work-orders/api/work-orders.api"
import { useRouter } from "next/navigation"
import successToast from "../../../shared/lib/toast/success.toast"
import errorToast from "../../../shared/lib/toast/error.toast"
import { useQueryClient } from "@tanstack/react-query"
import { WORK_ORDERS } from "@/shared/constants/backend-api"

interface WorkOrderDeleteDialogProps {
    workOrderId: number
    orderNumber?: string | null
}

function WorkOrderDeleteDialog({
    workOrderId,
    orderNumber,
}: WorkOrderDeleteDialogProps) {
    const [isDeleting, setIsDeleting] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const router = useRouter()
    const queryClient = useQueryClient()

    const handleDelete = async () => {
        setIsDeleting(true)
        try {
            const result = await deleteWorkOrder(workOrderId)
            if (result.success) {
                successToast("Work order deleted successfully")
                setIsOpen(false)
                // Invalidate the work orders query to trigger a refetch
                queryClient.invalidateQueries({ queryKey: [WORK_ORDERS.getAll] })
            } else {
                errorToast(result.message || "Failed to delete work order")
            }
        } catch (error) {
            console.error("Error deleting work order:", error)
            errorToast("An error occurred while deleting the work order")
        } finally {
            setIsDeleting(false)
        }
    }
    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    variant={"link"}
                    size={"icon"}
                    className="w-7 h-7 p-1.5 bg-destructive rounded-md hover:bg-destructive/90 cursor-pointer"
                >
                    <Trash className="w-2 text-white" />
                </Button>
            </DialogTrigger>
            <DialogContent className="px-10 gap-6">
                <Image
                    src={deleteIconPng}
                    alt="Delete Icon"
                    className="w-[80px] mx-auto"
                />
                <DialogTitle className="w-5/6 text-center mx-auto font-medium leading-snug">
                    {orderNumber ? `${orderNumber} - ` : ""}Are you sure you
                    want to delete this work order?
                </DialogTitle>
                <DialogFooter className="gap-3 md:gap-2">
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            className="w-full h-11 rounded-lg"
                            disabled={isDeleting}
                            showLoader={false}
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant={"destructive"}
                        className="w-full h-11 rounded-lg"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default WorkOrderDeleteDialog
